import paginationStyles from "../../../components/common/Pagination.module.css";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import TextInput from "../../../components/ui/TextInput";
import Dropdown from "../../../components/common/Dropdown";
import Button from "../../../components/ui/Button";
import { Tooltip } from "@mui/material";
import {
    getSoundLibraryAI,
    deleteSoundLibraryAI,
    getSoundLibraryAITypes,
    getFilteredSoundLibraryAICategories,
    getAllSoundLibraryAICategories,
} from "../../../api/soundLibrary";
import { SoundLibraryAI } from "../../../types/types";
import { SoundLibraryAITypeResponse, SoundLibraryAICategoryResponse } from "../../../types/requestsOutputs";
import AudioPlayerRewind from "../../../components/common/AudioPlayer";
import CreateSoundLibraryModal from "./CreateSoundLibraryModal";
import { useLanguage } from "../../../context/LanguageContext";
import Icon from "../../../components/common/Icon";
import { useNotification } from "../../../context/NotificationContext";
import styles from "./SoundLibraryAdmin.module.css";
import adminSurface from "../../../components/layouts/AdminSurface.module.css";
import { ModalPortal } from "../../../components/modals/ModalPortal";

const SoundLibraryList: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { language } = useLanguage();
    const { showNotification } = useNotification();

    const [library, setLibrary] = useState<SoundLibraryAI[]>([]);
    const [types, setTypes] = useState<SoundLibraryAITypeResponse[]>([]);
    const [categories, setCategories] = useState<SoundLibraryAICategoryResponse[]>([]);
    const [allCategories, setAllCategories] = useState<SoundLibraryAICategoryResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [typesLoading, setTypesLoading] = useState<boolean>(true);
    const [categoriesLoading, setCategoriesLoading] = useState<boolean>(true);
    const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

    const [search, setSearch] = useState<string>("");
    const [searchDebounced, setSearchDebounced] = useState<string>("");
    const [selectedType, setSelectedType] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [editItem, setEditItem] = useState<SoundLibraryAI | null>(null);
    const [selectedSound, setSelectedSound] = useState<SoundLibraryAI | null>(null);

    const [page, setPage] = useState<number>(1);
    const [limit] = useState<number>(10);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalItems, setTotalItems] = useState<number>(0);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounced(search);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        setPage(1);
        setSelectedSound(null);
    }, [searchDebounced, selectedType, selectedCategory]);

    const fetchLibrary = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token") || "";
            const response = await getSoundLibraryAI({
                token,
                language,
                searchQuery: searchDebounced,
                includeRewind: true,
                typeId: selectedType,
                categoryId: selectedCategory,
                page,
                limit,
            });
            setLibrary(response.items);
            setTotalItems(response.total);
            setTotalPages(response.totalPages);
        } catch (error) {
            console.error("Error fetching sound library:", error);
            showNotification({ message: t("errorFetchingSoundLibrary"), type: "error" });
        } finally {
            setLoading(false);
        }
    }, [language, selectedType, selectedCategory, searchDebounced, showNotification, t, page, limit]);

    const fetchCategories = useCallback(async () => {
        try {
            setCategoriesLoading(true);
            const token = localStorage.getItem("token") || "";
            const filteredCategories = await getFilteredSoundLibraryAICategories({
                token,
                language,
                includeRewind: true,
                searchQuery: searchDebounced,
                typeId: selectedType || null,
            });
            setCategories(filteredCategories);
        } catch (err) {
            console.error("Failed to fetch categories", err);
            showNotification({ message: t("errorFetchingSoundCategories"), type: "error" });
        } finally {
            setCategoriesLoading(false);
        }
    }, [language, searchDebounced, selectedType, showNotification, t]);

    useEffect(() => {
        fetchLibrary();

        const token = localStorage.getItem("token") || "";

        setTypesLoading(true);
        getSoundLibraryAITypes({ token, language })
            .then(setTypes)
            .catch((err) => {
                console.error("Failed to fetch types", err);
                showNotification({ message: t("errorFetchingSoundTypes"), type: "error" });
            })
            .finally(() => setTypesLoading(false));

        // Full, unfiltered category list used for assigning categories in the create/edit modal.
        // The filtered list (fetchCategories) only returns categories that already have sounds,
        // so it must not be used for the assignment dropdown.
        getAllSoundLibraryAICategories({ token, language })
            .then(setAllCategories)
            .catch((err) => {
                console.error("Failed to fetch all categories", err);
                showNotification({ message: t("errorFetchingSoundCategories"), type: "error" });
            });
    }, [fetchLibrary, language, showNotification, t]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    const handleDelete = async (id: string) => {
        try {
            setDeleteLoading(id);
            const token = localStorage.getItem("token") || "";
            await deleteSoundLibraryAI({ token, soundId: id });
            showNotification({ message: t("soundDeletedSuccessfully"), type: "success" });
            fetchLibrary();
        } catch (error) {
            console.error("Error deleting item:", error);
            showNotification({ message: t("errorDeletingSound"), type: "error" });
        } finally {
            setDeleteLoading(null);
        }
    };

    const handleModalSubmit = () => {
        showNotification({
            message: editItem ? t("soundUpdatedSuccessfully") : t("soundCreatedSuccessfully"),
            type: "success",
        });
        fetchLibrary();
    };

    const visiblePages = useMemo(() => {
        const range = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) range.push(i);
        } else {
            range.push(1);
            if (page > 3) range.push("...");
            for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
                range.push(i);
            }
            if (page < totalPages - 2) range.push("...");
            range.push(totalPages);
        }

        return range;
    }, [page, totalPages]);

    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, totalItems);

    const typeOptions = useMemo(() => types.map((type) => ({ value: type.id, label: type.localizedName })), [types]);
    const categoryOptions = useMemo(
        () => categories.map((cat) => ({ value: cat.id, label: cat.localizedName })),
        [categories],
    );
    const allCategoryOptions = useMemo(
        () => allCategories.map((cat) => ({ value: cat.id, label: cat.localizedName })),
        [allCategories],
    );

    useEffect(() => {
        if (selectedCategory && !categoryOptions.some((option) => option.value === selectedCategory)) {
            setSelectedCategory("");
        }
    }, [selectedCategory, categoryOptions]);

    return (
        <div className={`${styles.editableContainer}${selectedSound ? ` ${styles.editableContainerWithPlayer}` : ""}`}>
            <div className={`${styles.editableFiltersContainer} ${adminSurface.soundLibraryFiltersContainer}`}>
                <div className={`${styles.editableFiltersHeader} ${adminSurface.soundLibraryFiltersHeader}`}>
                    {t("filterSounds")}
                </div>
                <div className={`${styles.editableFiltersRow} ${adminSurface.soundLibraryFiltersRow}`}>
                    <div className={`${styles.editableSearch} ${adminSurface.soundLibrarySearchWrapper}`}>
                        <div className={styles.searchWrap}>
                            <Icon name="ic-search" className={styles.searchIcon} size={18} />
                            <TextInput
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t("searchByNameOrDescription")}
                                fullWidth
                            />
                        </div>
                    </div>
                    <div
                        className={`${styles.editableFilterDropdown} ${adminSurface.soundLibraryFilterDropdownWrapper}`}
                    >
                        <Dropdown
                            label=""
                            options={typeOptions}
                            value={selectedType}
                            onChange={setSelectedType}
                            placeholder={t("filterByType")}
                            loading={typesLoading}
                            error={false}
                            isWithoutMargin
                            fullWidth
                        />
                    </div>
                    <div
                        className={`${styles.editableFilterDropdown} ${adminSurface.soundLibraryFilterDropdownWrapper}`}
                    >
                        <Dropdown
                            label=""
                            options={categoryOptions}
                            value={selectedCategory}
                            onChange={setSelectedCategory}
                            placeholder={t("filterByCategory")}
                            loading={categoriesLoading}
                            error={false}
                            isWithoutMargin
                            fullWidth
                        />
                    </div>
                    <Button
                        label={t("createSound")}
                        variant="primary"
                        shape="default"
                        icon={<Icon name="ic-plus" className="ic" size={16} />}
                        className={styles.createSoundBtn}
                        onClick={() => {
                            setEditItem(null);
                            setIsModalOpen(true);
                        }}
                    />
                </div>
            </div>

            {loading || deleteLoading ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                </div>
            ) : library.length === 0 ? (
                <div className={styles.noItemsMessage}>{t("noSoundsFound")}</div>
            ) : (
                <>
                    <div className={styles.editableListBody}>
                        {library.map((item, index) => (
                            <div key={item.id || index} className={styles.editableItem}>
                                <button
                                    type="button"
                                    className={styles.play}
                                    onClick={() => setSelectedSound(item)}
                                    aria-label={t("play") || "Play"}
                                >
                                    <Icon name="ic-play" />
                                </button>
                                <div className={styles.editableName}>
                                    <Tooltip title={`${t("name")}: ${item.name}`} placement="bottom-start">
                                        <h4>{item.name}</h4>
                                    </Tooltip>
                                    <Tooltip
                                        title={`${t("description")}: ${item.soundDescription}`}
                                        placement="bottom-start"
                                    >
                                        <p>{item.soundDescription}</p>
                                    </Tooltip>
                                </div>
                                <div className={styles.editableMeta}>
                                    <Tooltip title={`${t("type")}: ${item.type}`} placement="bottom-start">
                                        <p>{item.type}</p>
                                    </Tooltip>
                                </div>
                                <div className={styles.editableMeta}>
                                    <Tooltip
                                        title={`${t("categories")}: ${item.categories?.map((c) => c.name).join(", ")}`}
                                        placement="bottom-start"
                                    >
                                        <p>{item.categories?.map((c) => c.name).join(", ") || "—"}</p>
                                    </Tooltip>
                                </div>
                                <div className={styles.editableEditButtons}>
                                    <button
                                        type="button"
                                        className={styles.btnMod}
                                        onClick={() => {
                                            setEditItem(item);
                                            setIsModalOpen(true);
                                        }}
                                    >
                                        {t("edit")}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.btnDel}
                                        onClick={() => handleDelete(item.id)}
                                        disabled={deleteLoading === item.id}
                                    >
                                        {t("remove")}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={paginationStyles.controls}>
                        <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                            &lt;
                        </button>
                        {visiblePages.map((p, idx) =>
                            p === "..." ? (
                                <span key={`ellipsis-${idx}`} className="ellipsis">
                                    ...
                                </span>
                            ) : (
                                <button
                                    key={p}
                                    className={p === page ? "active" : ""}
                                    onClick={() => setPage(p as number)}
                                >
                                    {p}
                                </button>
                            ),
                        )}
                        <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                            &gt;
                        </button>
                        <span className="pagination-total">
                            {t("paginationCounter")}: {`${startItem}–${endItem} / ${totalItems}`}
                        </span>
                    </div>
                </>
            )}

            {selectedSound && (
                <ModalPortal>
                    <div className={styles.playerFooter}>
                        <div className={styles.playerFooterInner}>
                            <AudioPlayerRewind
                                title={selectedSound.name}
                                src={selectedSound.url}
                                showAddtoProject={false}
                                isAutoPlay={true}
                                hideSeparator
                                embedded
                            />
                            <button
                                type="button"
                                className={styles.playerClose}
                                onClick={() => setSelectedSound(null)}
                                aria-label={t("close")}
                            >
                                <Icon name="ic-x" className="ic" size={18} />
                            </button>
                        </div>
                    </div>
                </ModalPortal>
            )}

            <CreateSoundLibraryModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditItem(null);
                }}
                onSubmit={handleModalSubmit}
                editItem={editItem}
                typeOptions={typeOptions}
                categoryOptions={allCategoryOptions}
            />
        </div>
    );
};

export default SoundLibraryList;
